import { FormEvent, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

const PASSWORD_POLICY =
  'Mot de passe : 10 caractères minimum, avec au moins une majuscule, une minuscule et un chiffre.';

function checkPolicy(pwd: string): string | null {
  if (pwd.length < 10) return PASSWORD_POLICY;
  if (!/[A-Z]/.test(pwd)) return PASSWORD_POLICY;
  if (!/[a-z]/.test(pwd)) return PASSWORD_POLICY;
  if (!/[0-9]/.test(pwd)) return PASSWORD_POLICY;
  return null;
}

type AdminUser = {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'editor' | 'reader';
  active: number;
  must_reset_password: number;
  last_seen_at: string | null;
  created_at: string;
};

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Création d'un user
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<'admin' | 'editor' | 'reader'>('editor');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset password modal
  const [resetUser, setResetUser] = useState<AdminUser | null>(null);
  const [resetPwd, setResetPwd] = useState('');

  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;

  async function load() {
    setLoading(true);
    try {
      const r = await api.get<{ users: AdminUser[] }>('/admin/users');
      setUsers(r.users);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function createUser(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const polErr = checkPolicy(newPassword);
    if (polErr) {
      setError(polErr);
      return;
    }
    setBusy(true);
    try {
      await api.post('/admin/users', {
        name: newName,
        email: newEmail,
        password: newPassword,
        role: newRole,
      });
      setShowCreate(false);
      setNewName('');
      setNewEmail('');
      setNewPassword('');
      setNewRole('editor');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Création impossible');
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(u: AdminUser) {
    if (u.id === user!.id) {
      alert('Tu ne peux pas désactiver ton propre compte.');
      return;
    }
    await api.patch(`/admin/users/${u.id}`, { active: !u.active });
    await load();
  }

  async function changeRole(u: AdminUser, role: 'admin' | 'editor' | 'reader') {
    await api.patch(`/admin/users/${u.id}`, { role });
    await load();
  }

  async function submitReset(e: FormEvent) {
    e.preventDefault();
    if (!resetUser) return;
    const polErr = checkPolicy(resetPwd);
    if (polErr) {
      setError(polErr);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/users/${resetUser.id}/reset`, { password: resetPwd });
      setResetUser(null);
      setResetPwd('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reset impossible');
    } finally {
      setBusy(false);
    }
  }

  async function forceReset(u: AdminUser) {
    if (!confirm(`Forcer ${u.name} à changer son mot de passe au prochain login ?`)) return;
    await api.patch(`/admin/users/${u.id}`, { must_reset_password: true });
    await load();
  }

  return (
    <>
      <div className="page-header">
        <h1>Utilisateurs</h1>
        <button className="btn btn-primary" onClick={() => setShowCreate((v) => !v)}>
          {showCreate ? 'Annuler' : 'Créer un utilisateur'}
        </button>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {showCreate && (
        <form className="card" style={{ marginBottom: 16 }} onSubmit={createUser}>
          <div className="card-title">Nouveau compte</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
            <div className="field">
              <label>Nom</label>
              <input value={newName} onChange={(e) => setNewName(e.target.value)} required />
            </div>
            <div className="field">
              <label>Email</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label>Rôle</label>
              <select value={newRole} onChange={(e) => setNewRole(e.target.value as any)}>
                <option value="admin">admin</option>
                <option value="editor">editor</option>
                <option value="reader">reader</option>
              </select>
            </div>
            <div className="field">
              <label>Mot de passe temporaire</label>
              <input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={10}
                placeholder="L'utilisateur devra le changer au 1er login"
              />
              <div style={{ fontSize: 11, color: 'var(--c-muted)', marginTop: 4 }}>
                {PASSWORD_POLICY}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Création…' : 'Créer le compte'}
            </button>
          </div>
        </form>
      )}

      <div className="card">
        {loading && <div>Chargement…</div>}
        {!loading && users.length === 0 && <div className="card-meta">Aucun utilisateur.</div>}
        {!loading &&
          users.map((u) => (
            <div
              key={u.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 200px 120px auto',
                gap: 12,
                alignItems: 'center',
                padding: '12px 0',
                borderTop: '1px solid var(--c-border)',
                opacity: u.active ? 1 : 0.55,
              }}
            >
              <div>
                <div style={{ fontWeight: 600 }}>
                  {u.name}
                  {u.id === user!.id && ' (toi)'}
                  {u.must_reset_password ? ' · 🔄 doit changer son MDP' : ''}
                </div>
                <div className="card-meta">{u.email}</div>
                <div className="card-meta" style={{ fontSize: 11 }}>
                  Créé le {u.created_at?.slice(0, 10)} · vu :{' '}
                  {u.last_seen_at?.slice(0, 16) ?? 'jamais'}
                </div>
              </div>
              <select
                value={u.role}
                onChange={(e) => changeRole(u, e.target.value as any)}
                disabled={u.id === user!.id}
                style={{ padding: 6 }}
              >
                <option value="admin">admin</option>
                <option value="editor">editor</option>
                <option value="reader">reader</option>
              </select>
              <button
                className={`btn ${u.active ? 'btn-ghost' : 'btn-valider'}`}
                onClick={() => toggleActive(u)}
                disabled={u.id === user!.id}
                style={{ minWidth: 100 }}
              >
                {u.active ? 'Désactiver' : 'Réactiver'}
              </button>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-ghost" onClick={() => forceReset(u)}>
                  Forcer reset
                </button>
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    setResetUser(u);
                    setResetPwd('');
                  }}
                >
                  Définir MDP
                </button>
              </div>
            </div>
          ))}
      </div>

      {resetUser && (
        <div className="export-modal" onClick={() => setResetUser(null)}>
          <div className="export-dialog" onClick={(e) => e.stopPropagation()}>
            <strong>Définir un mot de passe pour {resetUser.name}</strong>
            <form onSubmit={submitReset} style={{ marginTop: 12 }}>
              <div className="field">
                <label>Nouveau mot de passe</label>
                <input
                  type="text"
                  value={resetPwd}
                  onChange={(e) => setResetPwd(e.target.value)}
                  required
                  minLength={10}
                />
                <div style={{ fontSize: 11, color: 'var(--c-muted)', marginTop: 4 }}>
                  {PASSWORD_POLICY}
                </div>
                <div style={{ fontSize: 11, color: 'var(--c-muted)', marginTop: 6 }}>
                  L'utilisateur devra le changer au prochain login. Toutes ses sessions actives
                  seront fermées.
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setResetUser(null)}
                >
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  Appliquer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
